/**
 * Hover tooltip of a note in the lane (W2-03 note-lane; DESIGN.md §4.6 "Hover tooltip (600 ms): type, beat,
 * seconds, X, line, and Phira flags ('simultaneous', 'hidden under line', 'culled: negative speed', 'invisible:
 * before appear time')").
 *
 * The lane gestures set `session.tooltip` (note + pointer position); after 600 ms without another change the panel
 * shows this card next to the pointer. The Phira flags are the engine's: multi-highlight (Phira's exact rule) and,
 * at the playhead, the reason Phira hides the note if it does (the engine's ghost list of the frame at the
 * playhead, the same as the preview's "why is my note not visible" outlines).
 */

import { useEffect, useState } from 'react';
import { Beats } from '../../core/time/beat';
import type { GhostReason, FrameOptions, FrameDrawList } from '../../core/engine/types';
import type { NoteId } from '../../core/model/types';
import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { stackAt } from '../../render/timeline/laneHit';
import { formatX } from '../../render/timeline/laneDraw';
import { m } from './noteLane.messages';
import {
  altModifierText,
  beatCountText,
  beatText,
  laneSession,
  lineLabel,
  noteIndexOf,
  playheadSec,
  secText,
  tempoOf,
} from './laneSession';
import s from './LaneTooltip.module.css';

export const TOOLTIP_DELAY_MS = 600;

const REASON_KEY: Readonly<Record<GhostReason, string>> = {
  cover: m.flagCover,
  negativeLineAlpha: m.flagNegativeAlpha,
  culled: m.flagCulled,
  uiMoverLine: m.flagUiMover,
  beforeVisibleTime: m.flagBeforeAppear,
  offscreen: m.flagOffscreen,
  filtered: m.flagFiltered,
};

/** Reference window the flags are computed for (the preview letterboxes into any size; culling depends on it). */
const REF_W = 1920;
const REF_H = 1080;

let scratch: FrameDrawList | null = null;

/** Why Phira hides `note` at the playhead (null when it is drawn or the engine cannot tell). */
export function ghostReasonAt(services: AppServices, note: NoteId): GhostReason | null {
  const engine = services.engine;
  if (engine === null) return null;
  try {
    const e = services.editor.getState();
    const opts: FrameOptions = {
      winW: REF_W,
      winH: REF_H,
      player: e.preview.player,
      skin: services.skins.active.metrics,
      resAlpha: 1,
      hudProgress: 1,
      showHud: false,
      showEffects: false,
      showVideos: false,
      rate: e.rate,
      trackLengthSec: services.audio.music?.durationSec ?? engine.maxTime,
      filters: {
        hiddenLines: e.filters.hiddenLines,
        soloLines: e.filters.soloLines,
        mutedLayers: e.filters.mutedLayers,
        dim: null,
        focusHitsoundLines: e.filters.focusHitsoundLines,
      },
      ghosts: true,
      judge: null,
    };
    const list =
      scratch === null ? engine.frame(playheadSec(services), opts) : engine.frame(playheadSec(services), opts, scratch);
    scratch = list;
    return list.ghosts.find((g) => g.note === note)?.reason ?? null;
  } catch {
    return null;
  }
}

export interface TooltipContent {
  readonly title: string;
  readonly rows: readonly string[];
  readonly flags: readonly string[];
}

/** Lines of the tooltip of a note (translated). */
export function tooltipContent(
  services: AppServices,
  note: NoteId,
  t: (key: string, params?: Readonly<Record<string, string | number>>) => string,
): TooltipContent | null {
  const store = services.store;
  const n = store?.doc.notes.get(note);
  if (store === null || n === undefined) return null;
  const tempo = tempoOf(services);
  const kind = t(m.kindName, { kind: n.kind });
  const rows = [
    t(m.tipBeat, { beat: beatText(services, n.t) }),
    t(m.tipTime, { sec: secText(tempo === null ? 0 : tempo.secAt(n.t)) }),
    t(m.tipX, { x: formatX(n.x) }),
    t(m.tipLine, { line: lineLabel(services, store.doc, store.idx, n.line) }),
    t(m.tipSide, { above: n.above ? 'yes' : 'no' }),
  ];
  if (n.kind === 'hold') {
    const len = beatCountText(Beats.sub(n.end, n.t));
    rows.push(t(m.tipHoldEnd, { beat: beatText(services, n.end), length: len.text, one: len.one }));
  }
  const index = noteIndexOf(store, n.line);
  const i = index.indexOf(note);
  const stack = i < 0 ? 1 : stackAt(index, i).length;
  if (stack > 1) rows.push(t(m.tipStack, { count: stack, alt: altModifierText(services) }));
  const flags: string[] = [];
  const engine = services.engine;
  if (engine !== null) {
    try {
      if (engine.isMultiHighlight(note)) flags.push(t(m.flagSimultaneous));
    } catch {
      // engine not ready
    }
  }
  const reason = ghostReasonAt(services, note);
  if (reason !== null) flags.push(t(REASON_KEY[reason]));
  return { title: t(m.tipTitle, { kind, fake: n.fake ? 'yes' : 'no' }), rows, flags };
}

export function LaneTooltip(props: { readonly services: AppServices }) {
  const { services } = props;
  const t = useT();
  const target = laneSession(services).tooltip;
  const [shown, setShown] = useState<typeof target>(null);
  useEffect(() => {
    if (target === null) return undefined;
    const timer = setTimeout(() => {
      setShown(target);
    }, TOOLTIP_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [target]);
  if (shown === null || target === null || shown !== target) return null;
  const content = tooltipContent(services, shown.note, t);
  if (content === null) return null;
  const vw = typeof window === 'undefined' ? 1920 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 1080 : window.innerHeight;
  const left = Math.min(shown.clientX + 14, vw - 260);
  const top = Math.min(shown.clientY + 14, vh - 160);
  return (
    <div className={s.tip} role="tooltip" style={{ left, top }} data-note-tooltip={shown.note}>
      <div className={s.title}>{content.title}</div>
      {content.rows.map((r) => (
        <div key={r} className={s.row}>
          {r}
        </div>
      ))}
      {content.flags.map((f) => (
        <div key={f} className={s.flag}>
          {f}
        </div>
      ))}
    </div>
  );
}
